import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ElementRef, Injectable } from '@angular/core';
import { compile } from 'ejs';
import { isEmpty, noop } from 'lodash';
import {
    BestFitRate,
    ContractType,
    ContractTypeEnum,
    Contribution,
    GraphColors,
    Rate,
    spotInfoLabelSvgPath,
    contractInfoLabelSvgPath,
} from '../../models';
import * as R from 'ramda';
import { formatNumber } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class GraphBuilder {
    constructor() {}

    create13MonthHybridGraph(
        container: ElementRef<any>,
        contractRatesHistorical: Rate[],
        spotRatesHistorical: Rate[],
        contractData: Rate[],
        spotData: Rate[],
        contractType: ContractType,
        bestFitRate: BestFitRate,
        perMile: boolean,
    ) {
        // To be implemented later
        var root = am5.Root.new(container.nativeElement, {
            tooltipContainerBounds: {
                top: 300,
                bottom: 100,
                left: 50,
                right: 50,
            },
        });

        let parentContainer = root.container.children.push(
            am5.Container.new(root, {
                width: am5.percent(100),
                height: am5.percent(100),
                layout: root.horizontalLayout,
            }),
        );
        let chartContainer1 = parentContainer.children.push(
            am5.Container.new(root, {
                width: am5.percent(92),
                height: am5.percent(100),
            }),
        );
        let chartContainer2 = parentContainer.children.push(
            am5.Container.new(root, {
                width: am5.percent(8),
                height: am5.percent(100),
            }),
        );
        var chart = chartContainer1.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );

        const bestFitChart = chartContainer2.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );
        if (isEmpty(contractData) && isEmpty(spotData)) return chart;
        const forecastData = this.getGraphData(
            this.normalizeForecastDates(contractData)?.sort(
                this.sortDataByDateAsc,
            ),
            this.normalizeForecastDates(spotData)?.sort(this.sortDataByDateAsc),
            contractType,
        );

        const historicalData = this.getGraphData(
            contractRatesHistorical?.sort(this.sortDataByDateAsc),
            spotRatesHistorical?.sort(this.sortDataByDateAsc),
            contractType,
        );

        const { min, max } = this.getMinMaxYValues(
            [
                spotData,
                contractData,
                contractRatesHistorical,
                spotRatesHistorical,
                [bestFitRate as any],
            ],
            [],
        );

        const yAxis = this.createYAxis(chart, root, perMile, min, max);

        yAxis.get('renderer').grid.template.setAll({
            strokeDasharray: [4],
        });

        yAxis.get('renderer').labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        const bestFitYAxis = this.createYAxis(
            bestFitChart,
            root,
            perMile,
            min,
            max,
            {
                visible: false,
            },
        );

        bestFitYAxis.get('renderer').grid.template.setAll({
            strokeDasharray: [4],
        });

        this.create7DayLine(
            bestFitChart,
            root,
            bestFitYAxis,
            bestFitRate,
            contractType,
            historicalData.default.color,
        );

        const bestFitCursor = bestFitChart.get('cursor');
        bestFitCursor.lineX.setAll({
            dy: -80,
        });

        const bestFitXAxis = bestFitChart.xAxes.getIndex(0);
        bestFitXAxis.labelsContainer.setAll({
            position: 'absolute',
            y: -70,
        });
        // Add correct spacing below best fit line
        const legendSpacingContainer = am5.Container.new(root, {
            layout: root.horizontalLayout,
            height: 10,
        });
        bestFitChart.children.push(legendSpacingContainer);

        // Create X-Axis
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {
                    tooltip: am5.Tooltip.new(root, {
                        themeTags: ['axis'],
                        forceHidden: true,
                    }),
                }),
                baseInterval: {
                    timeUnit: 'month',
                    count: 1,
                },
                markUnitChange: false,
                gridIntervals: [{ timeUnit: 'month', count: 1 }],
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
            }),
        );

        // xAxis.labelsContainer.set('position', 'absolute');

        const xRenderer = xAxis.get('renderer');
        xRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });

        xAxis.labelsContainer.setAll({
            position: 'absolute',
            y: -60,
        });
        xRenderer.labels.template.setAll({
            dx: 30,
            paddingTop: 30,
            rotation: 60,
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });
        xAxis.get('dateFormats')['month'] = 'MMM';

        const {
            lineSeries: historicalLineSeries,
            candlestickSeries: historicalCandlestick,
        } = this.createHistoricalSeries(
            root,
            chart,
            xAxis,
            yAxis,
            historicalData.default.data,
            historicalData.default.color,
            historicalData.default.contractType,
            historicalData.default.contractType === 'CONTRACT'
                ? 'Historical Contract'
                : 'Historical Spot',
            false,
        );

        const {
            dottedLineSeries: forecastDottedLine,
            areaLineSeries: forecastAreaSeries,
        } = this.createForecastSeries(
            root,
            chart,
            xAxis,
            yAxis,
            forecastData.default.data,
            forecastData.default.color,
            forecastData.default.contractType,
            forecastData.default.contractType === 'CONTRACT'
                ? 'Forecast Contract'
                : 'Forecast Spot',
        );

        const {
            lineSeries: secondaryHistoricalLineSeries,
            candlestickSeries: secondaryHistoricalCandlestick,
        } = this.createHistoricalSeries(
            root,
            chart,
            xAxis,
            yAxis,
            historicalData.secondary.data,
            historicalData.secondary.color,
            historicalData.secondary.contractType,
            historicalData.secondary.contractType === 'CONTRACT'
                ? 'Historical Contract'
                : 'Historical Spot',
            true,
        );

        const {
            dottedLineSeries: secondaryForecastDottedLine,
            areaLineSeries: secondaryForecastAreaSeries,
        } = this.createForecastSeries(
            root,
            chart,
            xAxis,
            yAxis,
            forecastData.secondary.data,
            forecastData.secondary.color,
            forecastData.secondary.contractType,
            forecastData.secondary.contractType === 'CONTRACT'
                ? 'Forecast Contract'
                : 'Forecast Spot',
        );

        forecastDottedLine.data.setAll(
            forecastData.default.data.map((rate) => {
                const forecastYear = new Date(rate.date);
                forecastYear.setFullYear(forecastYear.getFullYear() + 1);
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createContractTooltip()
                            : this.createSpotTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: forecastYear.toLocaleString('en-US', {
                            timeZone: 'UTC',
                            year: 'numeric',
                            month: 'short',
                        }),
                        title: `Forecast ${
                            contractType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                };
            }),
        );

        historicalLineSeries.data.setAll(
            historicalData.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createContractTooltip()
                            : this.createSpotTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            timeZone: 'UTC',
                            year: 'numeric',
                            month: 'short',
                        }),
                        title: `Historical ${
                            contractType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                };
            }),
        );

        forecastAreaSeries.data.setAll(
            forecastData.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.high,
                    openY: rate.low,
                };
            }),
        );

        const tooltip: am5.Tooltip = historicalLineSeries.get('tooltip');

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div></div>');
        chart.plotContainer.set('tooltip', tooltip);
        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });
        tooltip.label.adapters.add('dy', (dy) => {
            historicalLineSeries
                .get('tooltip')
                .adapters.add('bounds', function () {
                    return {
                        left: 70,
                        right: 9999,
                        top: 0,
                        bottom: 20,
                    };
                });
            return -tooltip._fy;
        });

        historicalLineSeries
            .get('tooltip')
            .label.adapters.add('html', function (text, _target) {
                let tooltips = [];
                text = '';
                chart.series.each(function (series, index) {
                    var tooltipDataItem = series.get('tooltipDataItem');
                    const template = tooltipDataItem?.dataContext?.['template'];
                    if (!!template?.length && series.isVisible()) {
                        tooltips.push(
                            `<div class="flex flex-col items-center">${template}</div>`,
                        );
                        if (tooltips.length % 2 === 0) {
                            text += `<div class="-mt-1 mb-2 flex flex-row items-center space-x-10">${tooltips.join(
                                '',
                            )}</div>`;
                            tooltips = [];
                        }
                    }
                });
                if (tooltips.length > 0) {
                    text += `<div class="mb-2 flex flex-col items-center">${tooltips.join(
                        '',
                    )}</div>`;
                    tooltips = [];
                }
                return `<div class="flex flex-col items-center gap-4">${text}</div>`;
            });

        historicalLineSeries
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(historicalData.default.color.hex),
                strokeOpacity: 1,
                fillOpacity: 1,
            });

        forecastDottedLine
            .get('tooltip')
            .label.adapters.add('html', function (text, _target) {
                let tooltips = [];
                text = '';

                return ``;
            });
        forecastDottedLine
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 0,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color('#FFFFF'),
                strokeOpacity: 0,
                fillOpacity: 0,
            });

        secondaryForecastDottedLine.data.setAll(
            forecastData.secondary.data.map((rate) => {
                const forecastYear = new Date(rate.date);
                forecastYear.setFullYear(forecastYear.getFullYear() + 1);
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createSpotTooltip()
                            : this.createContractTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: forecastYear.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                        }),
                        title: `Forecast ${
                            contractType === 'CONTRACT' ? 'Spot' : 'Contract'
                        }`,
                    }),
                };
            }),
        );

        secondaryHistoricalLineSeries.data.setAll(
            historicalData.secondary.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createSpotTooltip()
                            : this.createContractTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            timeZone: 'UTC',
                        }),
                        title: `Historical ${
                            contractType === 'CONTRACT' ? 'Spot' : 'Contract'
                        }`,
                    }),
                };
            }),
        );

        secondaryForecastAreaSeries.data.setAll(
            forecastData.secondary.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.high,
                    openY: rate.low,
                };
            }),
        );

        this.addCursor(
            root,
            [historicalLineSeries, forecastDottedLine],
            chart,
            -85,
            xAxis,
        );

        this.createHybridLegend(
            chart,
            root,
            forecastDottedLine,
            forecastAreaSeries,
            historicalLineSeries,
            historicalCandlestick,
            secondaryForecastDottedLine,
            secondaryForecastAreaSeries,
            secondaryHistoricalLineSeries,
            secondaryHistoricalCandlestick,
            contractType,
        );
        secondaryHistoricalLineSeries.hide();
        secondaryHistoricalCandlestick.hide();
        secondaryForecastAreaSeries.hide();
        secondaryForecastDottedLine.hide();

        // edits the tooltip stroke color based on what is hovered
        historicalLineSeries
            .get('tooltip')
            .get('background')
            .adapters.add('stroke', function (_fill, _target) {
                var toReturn;
                var tooltipDataItem =
                    historicalLineSeries.get('tooltipDataItem');
                if (
                    secondaryForecastDottedLine?.isHidden() &&
                    secondaryHistoricalLineSeries?.isHidden()
                ) {
                    toReturn = am5.color(forecastData.default.color.hex);
                } else if (
                    tooltipDataItem?.dataContext?.['date'] <
                        tooltipDataItem?.component?.dataItems[1]?.dataContext?.[
                            'date'
                        ] &&
                    secondaryHistoricalLineSeries?.isHidden()
                ) {
                    toReturn = am5.color(forecastData.default.color.hex);
                } else if (
                    secondaryForecastDottedLine?.isHidden() &&
                    tooltipDataItem === undefined
                ) {
                    toReturn = am5.color(forecastData.default.color.hex);
                } else {
                    toReturn = am5.color('#636D79');
                }

                return toReturn;
            });

        return chart;
    }

    private create7DayLine(
        chart: am5xy.XYChart,
        root: am5.Root,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        rate: BestFitRate,
        rateType: ContractType,
        color: { hex; name },
    ) {
        const categoryAxis = chart.xAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: 'category',
                renderer: am5xy.AxisRendererX.new(root, {}),
                width: 80,
                paddingTop: 5,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
            }),
        );

        categoryAxis.data.setAll([
            {
                category: rate.escalation,
            },
        ]);

        const axisRenderer = categoryAxis.get('renderer');
        axisRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });

        axisRenderer.labels.template.setAll({
            rotation: 60,
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
            paddingTop: 10,
            location: 0.8,
        });
        const dayNumber = this.mapEscalationToNumber(rate.escalation);
        axisRenderer.labels.template.adapters.add(
            'text',
            function (_text, _target) {
                return `Last ${dayNumber} \n Days`;
            },
        );
        var catSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                xAxis: categoryAxis,
                yAxis: yAxis,
                valueYField: 'value',
                categoryXField: 'category',
                openValueYField: 'openY',
            }),
        );

        this.addSquare(catSeries, root, color.hex);

        catSeries.data.setAll([
            {
                category: rate.escalation,
                value: rate.average,
                high: rate.high,
                low: rate.low,
            },
        ]);

        const candlestickSeries = chart.series.push(
            am5xy.CandlestickSeries.new(root, {
                name: 'Candlesticks',
                xAxis: categoryAxis,
                yAxis,
                categoryXField: 'category',
                openValueYField: 'open',
                highValueYField: 'high',
                lowValueYField: 'low',
                valueYField: 'close',
                clustered: false,
                tooltip: am5.Tooltip.new(root, {
                    getFillFromSprite: false,
                    labelHTML: compile(
                        rateType === 'CONTRACT'
                            ? this.createContractTooltip()
                            : this.createSpotTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: `Last ${dayNumber} Days`,
                        title: `Historical ${
                            rateType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                }),
            }),
        );
        candlestickSeries
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(color.hex),
                strokeOpacity: 1,
                fillOpacity: 1,
            });

        candlestickSeries.get('tooltip').label.adapters.add('dy', (dy) => {
            candlestickSeries
                .get('tooltip')
                .adapters.add('bounds', function () {
                    return {
                        left: 70,
                        right: 9999,
                        top: 200,
                        bottom: 20,
                    };
                });
            return dy;
        });
        candlestickSeries.get('tooltip').adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 20,
            };
        });

        this.setCandlestickColor(candlestickSeries, color.hex);

        this.addBottomLine(candlestickSeries, root, color.hex)
            .addTopLine(candlestickSeries, root, color.hex)
            .setWidth(candlestickSeries);

        candlestickSeries.data.setAll([
            {
                open: rate.high,
                close: rate.low,
                high: rate.high,
                low: rate.low,
                category: rate.escalation,
            },
        ]);

        this.addCursor(root, [candlestickSeries], chart, -10, categoryAxis);

        return chart;
    }
    mapEscalationToNumber(escalation: string) {
        return {
            ONEYEAR: '365',
            ONEHUNDREDEIGHTYDAYS: '180',
            NINETYDAYS: '90',
            SIXTYDAYS: '60',
            ONEMONTH: '30',
            THIRTYDAYS: '30',
            FIFTEENDAYS: '15',
            SEVENDAYS: '7',
            THREEDAYS: '3',
        }[escalation];
    }

    create13MonthForecastGraph(
        container: ElementRef<any>,
        contractData: Rate[],
        spotData: Rate[],
        contractType: ContractTypeEnum,
        perMile: boolean,
    ) {
        const root = am5.Root.new(container.nativeElement, {
            tooltipContainerBounds: {
                top: 300,
                bottom: 100,
                left: 50,
                right: 50,
            },
        });
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );

        if (isEmpty(contractData) && isEmpty(spotData)) return chart;

        const data = this.getGraphData(
            contractData?.sort(this.sortDataByDateAsc),
            spotData?.sort(this.sortDataByDateAsc),
            contractType,
        );

        const { min, max } = this.getMinMaxYValues(
            [contractData, spotData],
            [],
        );

        // Create Y-axis
        const yAxis = this.createYAxis(chart, root, perMile, min, max);
        yAxis.get('renderer').grid.template.setAll({
            strokeDasharray: [4],
        });

        yAxis.get('renderer').labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        // Create X-Axis
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {}),
                baseInterval: {
                    timeUnit: 'month',
                    count: 1,
                },
                gridIntervals: [{ timeUnit: 'month', count: 1 }],
                paddingTop: 6,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
                dateFormats: {
                    month: "MMM ''yy",
                },
                markUnitChange: false,
            }),
        );
        const xRenderer = xAxis.get('renderer');
        xRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });
        xRenderer.labels.template.setAll({
            rotation: 60,
            fontSize: 12,
            dx: -10,
            paddingTop: -30,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        const {
            dottedLineSeries: defaultDottedLine,
            areaLineSeries: defaultAreaSeries,
        } = this.createForecastSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data.default.data,
            data.default.color,
            data.default.contractType,
            data.default.contractType === 'CONTRACT'
                ? 'Forecast Contract'
                : 'Forecast Spot',
        );

        const {
            dottedLineSeries: secondaryDottedLine,
            areaLineSeries: secondaryAreaSeries,
        } = this.createForecastSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data.secondary.data,
            data.secondary.color,
            data.secondary.contractType,
            data.secondary.contractType === 'CONTRACT'
                ? 'Forecast Contract'
                : 'Forecast Spot',
        );

        defaultDottedLine.data.setAll(
            data.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createContractTooltip()
                            : this.createSpotTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            timeZone: 'UTC',
                            year: 'numeric',
                            month: 'long',
                        }),
                        title: `Forecast ${
                            contractType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                };
            }),
        );

        defaultAreaSeries.data.setAll(
            data.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.high,
                    openY: rate.low,
                };
            }),
        );

        const tooltip: am5.Tooltip = defaultDottedLine.get('tooltip');

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div></div>');
        chart.plotContainer.set('tooltip', tooltip);
        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });
        tooltip.label.adapters.add('dy', (dy) => {
            defaultDottedLine
                .get('tooltip')
                .adapters.add('bounds', function () {
                    return {
                        left: 70,
                        right: 9999,
                        top: 0,
                        bottom: 20,
                    };
                });
            return -tooltip._fy;
        });

        defaultDottedLine
            .get('tooltip')
            .label.adapters.add('html', function (text, _target) {
                text = '';
                chart.series.each(function (series) {
                    const tooltipDataItem = series.get('tooltipDataItem');
                    const template = tooltipDataItem?.dataContext?.['template'];
                    if (!!template?.length && series.isVisible()) {
                        text += tooltipDataItem.dataContext['template'];
                    }
                });
                return `<div class="-mt-1 mb-2 flex flex-row gap-4">${text}</div>`;
            });

        defaultDottedLine
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(data.default.color.hex),
                strokeOpacity: 1,
                fillOpacity: 1,
            });

        defaultDottedLine
            .get('tooltip')
            .get('background')
            .adapters.add('stroke', function (_fill, _target) {
                if (secondaryDottedLine?.isHidden()) {
                    return am5.color(data.default.color.hex);
                }
                return am5.color('#636D79');
            });

        secondaryDottedLine.data.setAll(
            data.secondary.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createSpotTooltip()
                            : this.createContractTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                        }),
                        title: `Forecast ${
                            contractType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                };
            }),
        );

        secondaryAreaSeries.data.setAll(
            data.secondary.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.high,
                    openY: rate.low,
                };
            }),
        );

        this.addCursor(root, [defaultDottedLine], chart, -15, xAxis);

        this.createLegend(
            chart,
            root,
            defaultDottedLine,
            secondaryDottedLine,
            secondaryAreaSeries,
            true,
            data.default.contractType,
            '',
            null,
        );
        secondaryDottedLine.hide();
        secondaryAreaSeries.hide();
        return chart;
    }

    create35DayForecastGraph(
        container: ElementRef<any>,
        spotData: Rate[],
        perMile: boolean,
    ) {
        const root = am5.Root.new(container.nativeElement, {
            tooltipContainerBounds: {
                top: 300,
                bottom: 100,
                left: 50,
                right: 50,
            },
        });
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );

        if (isEmpty(spotData)) return chart;

        const data = this.getGraphData(
            [],
            spotData.sort(this.sortDataByDateAsc),
            ContractTypeEnum.Spot,
        );

        const { min, max } = this.getMinMaxYValues([spotData], []);

        // Create Y-axis
        const yAxis = this.createYAxis(chart, root, perMile, min, max);

        const yRenderer = yAxis.get('renderer');
        yRenderer.grid.template.setAll({
            strokeDasharray: [4],
        });

        yRenderer.labels.template.setAll({
            fontSize: 10,
            fontFamily: 'Sequel Sans',
        });

        yAxis.get('renderer').labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        // Create X-Axis
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {}),
                baseInterval: {
                    timeUnit: 'day',
                    count: 1,
                },
                gridIntervals: [{ timeUnit: 'day', count: 1 }],
                markUnitChange: false,
                paddingTop: 6,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
            }),
        );
        const xRenderer = xAxis.get('renderer');
        xRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });
        xRenderer.labels.template.setAll({
            dx: -10,
            paddingTop: -30,
            rotation: 60,
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });
        xAxis.get('dateFormats')['day'] = 'MM/dd/yy';

        const {
            dottedLineSeries: defaultDottedLine,
            areaLineSeries: defaultAreaSeries,
        }: {
            dottedLineSeries: am5xy.LineSeries;
            areaLineSeries: am5xy.LineSeries;
        } = this.createForecastSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data.default.data,
            data.default.color,
            data.default.contractType,
            data.default.contractType === 'CONTRACT'
                ? 'Forecast Contract'
                : 'Forecast Spot',
        );

        defaultDottedLine.data.setAll(
            data.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, false),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(this.createSpotTooltip())({
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            day: '2-digit',
                            year: 'numeric',
                            month: 'long',
                            timeZone: 'UTC',
                        }),
                        title: `Forecast Spot`,
                    }),
                };
            }),
        );

        defaultAreaSeries.data.setAll(
            data.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, false),
                    value: rate.high,
                    openY: rate.low,
                };
            }),
        );

        const tooltip: am5.Tooltip = defaultDottedLine.get('tooltip');

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div></div>');
        chart.plotContainer.set('tooltip', tooltip);
        tooltip.adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 0,
            };
        });
        tooltip.label.adapters.add('dy', (dy) => {
            defaultDottedLine
                .get('tooltip')
                .adapters.add('bounds', function () {
                    return {
                        left: 70,
                        right: 9999,
                        top: 0,
                        bottom: 20,
                    };
                });
            return -tooltip._fy;
        });

        defaultDottedLine
            .get('tooltip')
            .label.adapters.add('html', function (text, _target) {
                text = '';
                chart.series.each(function (series) {
                    var tooltipDataItem = series.get('tooltipDataItem');
                    const template = tooltipDataItem?.dataContext?.['template'];
                    if (!!template?.length && series.isVisible()) {
                        text += tooltipDataItem.dataContext['template'];
                    }
                });
                return `<div class="-mt-1 mb-2 flex flex-row gap-4">${text}</div>`;
            });

        defaultDottedLine
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(data.default.color.hex),
                strokeOpacity: 1,
                fillOpacity: 1,
            });

        defaultDottedLine
            .get('tooltip')
            .get('background')
            .adapters.add('stroke', function (_fill, _target) {
                return am5.color(data.default.color.hex);
            });

        this.addCursor(root, [defaultDottedLine], chart, -10, xAxis);

        const legend = chart.children.push(
            am5.Legend.new(root, { clickTarget: 'none' }),
        );

        legend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        legend.labels.template.setup = this._helpIcon(
            root,
            data.default.contractType,
        );
        legend.labels.template.set(
            'tooltipText',
            '[bold fontSize: 12px]The shaded area represents Median Absolute Error (MAE)[/]',
        );
        legend.labels.template.set('tooltipPosition', 'pointer');
        legend.labels.template.states.create('hover', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#0032A8'),
        });

        legend.data.setAll([defaultDottedLine]);

        return { root, chart, xAxis, yAxis };
    }

    getGraphData(
        contractData: Rate[] = [],
        spotData: Rate[] = [],
        contractType: ContractTypeEnum,
    ) {
        return {
            default: {
                contractType: contractType,
                color:
                    contractType === 'CONTRACT'
                        ? { hex: GraphColors.Contract, name: 'dat-blue-400' }
                        : { hex: GraphColors.Spot, name: 'dat-flame-400' },
                data: contractType === 'CONTRACT' ? contractData : spotData,
            },
            secondary: {
                contractType: (contractType === 'CONTRACT'
                    ? 'SPOT'
                    : 'CONTRACT') as ContractTypeEnum,
                color:
                    contractType === 'CONTRACT'
                        ? { hex: GraphColors.Spot, name: 'dat-flame-400' }
                        : { hex: GraphColors.Contract, name: 'dat-blue-400' },
                data: contractType === 'CONTRACT' ? spotData : contractData,
            },
        };
    }

    // Subtract 1 year from forecast dates in order to align with historical data
    normalizeForecastDates(data: Rate[]): Rate[] {
        const newData = data.map((rate) => {
            const d = new Date(rate.date);
            d.setFullYear(d.getFullYear() - 1);
            return {
                ...rate,
                date: d.toISOString(),
            };
        });
        return newData;
    }
    createForecastSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis:
            | am5xy.DateAxis<am5xy.AxisRenderer>
            | am5xy.CategoryAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        data: Rate[],
        color: { hex: GraphColors; name: string },
        contractType: ContractTypeEnum,
        name: string,
    ): {
        dottedLineSeries: am5xy.LineSeries;
        areaLineSeries: am5xy.LineSeries;
    } {
        const dottedLineSeries = this.createLineSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data,
            color.hex,
            contractType,
            name,
        );

        dottedLineSeries.strokes.template.set('strokeDasharray', [3, 4]);

        const areaLineSeries = this.createLineSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data,
            color.hex,
            contractType,
            name,
        );
        areaLineSeries.strokes.template.setAll({
            strokeWidth: 0,
            opacity: 0,
        });
        areaLineSeries.fills.template.setAll({
            fillOpacity: 0.1,
            visible: true,
        });

        return { dottedLineSeries, areaLineSeries };
    }

    createSpotTooltip() {
        return `
        <div class="bg-white flex flex-col justify-center">
            <label class="text-center text-dat-flame-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
            <div class="text-center text-dat-grey-900 pb-1 font-caption">
                <%= date %>
            </div>
            <div class="text-center font-caption-strong text-dat-grey-900">
                <%= average %>
            </div>
            <div class="text-center font-caption text-dat-grey-900">
                <%= low %>-<%= high %>
            </div>
        </div>

    `;
    }
    createContractTooltip() {
        return `
        <div class="bg-white flex flex-col justify-center">
            <label class="text-center text-dat-blue-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
            <div class="text-center font-caption text-dat-grey-900 pb-1">
                <%= date %>
            </div>
            <div class="text-center font-caption-strong text-dat-grey-900">
                <%= average %>
            </div>
            <div class="text-center font-caption text-dat-grey-900">
                <%= low %>-<%= high %>
            </div>
        </div>

    `;
    }

    createTooltipContribution() {
        return `
        <div class="bg-white flex flex-col justify-center">
            <div class="text-center font-caption text-dat-grey-900 pb-1">
                <%= date %>
            </div>
            <div class="font-caption text-dat-grey-900">
            <%=userType === 'Broker' ? 'Shipper' : userType === 'Broker' ? 'Carrier' : userType === 'Carrier' && contractType === 'SPOT' ? 'Broker' : 'Shipper' %>: <strong><%= !!name ? name : '--' %></strong>
            </div>
            <div class="text-center font-caption text-dat-grey-900">
            <%= (userType === 'Broker' && contractType === 'SPOT') || (userType === 'Shipper') ? 'Paid to Carrier' : 'Received' %>: <strong><%= value %></strong>
            </div>
        </div>

    `;
    }

    createYAxis(
        chart: am5xy.XYChart,
        root: am5.Root,
        perMile: boolean,
        min: number,
        max: number,
        settings: any = {},
    ) {
        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
                height: 260,
                autoZoom: false,
                numberFormat: perMile ? '$#,###.00' : '$#,###',
                paddingRight: 6,
                marginLeft: -8, // Default margin is taking around 24px.
                min: min,
                max: max,
                ...settings,
            }),
        );

        yAxis.axisRanges.clear();
        const rangeStep: number = perMile ? 0.5 : 500;
        const rangeMin: number = perMile
            ? Math.ceil(min * 2) / 2 - 0.5
            : min * 0.1;
        const rangeMax: number = perMile
            ? Math.floor(max * 2) / 2 + 0.5
            : max * 0.05;
        this._rangeStep(rangeMin, rangeMax, rangeStep).forEach((n: number) => {
            const dataItem = yAxis.makeDataItem({
                value: n,
            });

            const range = yAxis.createAxisRange(dataItem);

            range.get('label').setAll({
                forceHidden: false,
                text: `$${formatNumber(n, 'en-US', '1.2-2')}`,
                fill: am5.color('#636D79'),
                fontSize: 12,
            });

            range.get('grid').setAll({
                forceHidden: false,
                strokeDasharray: [4],
                strokeWidth: 1,
                location: 1,
            });
        });
        return yAxis;
    }

    private _rangeStep(start: number, end: number, step): number[] {
        return R.unfold((n) => (n <= end ? [n, n + step] : false), start);
    }

    setUpGraph(container: ElementRef, rootId: string) {
        am5.array.each(am5.registry.rootElements, function (r) {
            if (r?.dom?.id == rootId) {
                r.dispose();
            }
        });
        const root = am5.Root.new(container.nativeElement, {
            tooltipContainerBounds: {
                top: 300,
                bottom: 100,
                left: 50,
                right: 50,
            },
        });

        return {
            root,
        };
    }

    create13MonthHistoricalGraph(
        rootId: string,
        container: ElementRef<any>,
        contractData: Rate[],
        spotData: Rate[],
        contributions: Contribution[],
        contractType: ContractTypeEnum,
        bestFitRate: BestFitRate,
        perMile: boolean,
        hideContributions: boolean,
        dbId: string,
    ): am5xy.XYChart {

        const {root} = this.setUpGraph(container, rootId);
        let parentContainer = root.container.children.push(
            am5.Container.new(root, {
                width: am5.percent(100),
                height: am5.percent(100),
                layout: root.horizontalLayout,
            }),
        );
        let chartContainer1 = parentContainer.children.push(
            am5.Container.new(root, {
                width: am5.percent(92),
                height: am5.percent(100),
            }),
        );
        let chartContainer2 = parentContainer.children.push(
            am5.Container.new(root, {
                width: am5.percent(8),
                height: am5.percent(100),
            }),
        );
        const chart = chartContainer1.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );

        const bestFitChart = chartContainer2.children.push(
            am5xy.XYChart.new(root, {
                panY: false,
                layout: root.verticalLayout,
            }),
        );
        if (isEmpty(contractData) && isEmpty(spotData)) return chart;
        const data = this.getGraphData(
            contractData?.sort(this.sortDataByDateAsc),
            spotData?.sort(this.sortDataByDateAsc),
            contractType,
        );

        const { min, max } = this.getMinMaxYValues(
            [spotData, contractData, [bestFitRate as any]],
            contributions,
        );

        // Create Y-axis
        const yAxis = this.createYAxis(chart, root, perMile, min, max);

        yAxis.get('renderer').grid.template.setAll({
            strokeDasharray: [4],
        });

        yAxis.get('renderer').labels.template.setAll({
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        const bestFitYAxis = this.createYAxis(
            bestFitChart,
            root,
            perMile,
            min,
            max,
            {
                visible: false,
            },
        );

        bestFitYAxis.get('renderer').grid.template.setAll({
            strokeDasharray: [4],
        });

        this.create7DayLine(
            bestFitChart,
            root,
            bestFitYAxis,
            bestFitRate,
            contractType,
            data.default.color,
        );

        // Spacing container so best fit chart lines up with main chart
        const legendSpacingContainer = am5.Container.new(root, {
            layout: root.horizontalLayout,
            height: 25,
        });
        bestFitChart.children.push(legendSpacingContainer);

        // Create X-Axis
        // Monthly
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {}),
                baseInterval: {
                    timeUnit: 'month',
                    count: 1,
                },
                gridIntervals: [{ timeUnit: 'month', count: 1 }],
                paddingTop: 6,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
                dateFormats: {
                    month: "MMM ''yy",
                },
                markUnitChange: false,
            }),
        );
        const xRenderer = xAxis.get('renderer');
        xRenderer.grid.template.setAll({
            strokeWidth: 0,
            strokeOpacity: 0,
        });
        xRenderer.labels.template.setAll({
            dx: 10,
            rotation: 60,
            fontSize: 12,
            fill: am5.color('#636D79'),
            fontFamily: 'Sequel Sans',
        });

        // Daily
        const dailyAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                renderer: am5xy.AxisRendererX.new(root, {}),
                min: this.mapDateTime(data?.default?.data?.[0]?.date, true),
                max: this.mapDateTime(data?.default?.data?.[12]?.date, true),
                baseInterval: {
                    timeUnit: 'day',
                    count: 1,
                },
                visible: false,
                gridIntervals: [{ timeUnit: 'month', count: 1 }],
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
            }),
        );

        const xDailyRenderer = dailyAxis.get('renderer');
        xDailyRenderer.grid.template.setAll({
            height: 0,
            maxHeight: 0,
            strokeWidth: 0,
            strokeOpacity: 0,
        });

        const {
            lineSeries: defaultLineSeries,
            candlestickSeries: defaultCandlestick,
        } = this.createHistoricalSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data.default.data,
            data.default.color,
            contractType,
            contractType === 'CONTRACT'
                ? 'Historical Contract'
                : 'Historical Spot',
            false,
        );

        defaultLineSeries?.data?.setAll(
            data.default.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createContractTooltip()
                            : this.createSpotTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            timeZone: 'UTC',
                            year: 'numeric',
                            month: 'long',
                        }),
                        title: `Historical ${
                            contractType === 'CONTRACT' ? 'Contract' : 'Spot'
                        }`,
                    }),
                };
            }),
        );

        const {
            lineSeries: secondaryLineSeries,
            candlestickSeries: secondaryCandlestick,
        } = this.createHistoricalSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data.secondary.data,
            data.secondary.color,
            contractType,
            contractType === 'CONTRACT'
                ? 'Historical Spot'
                : 'Historical Contract',
            true,
        );

        secondaryLineSeries?.data?.setAll(
            data.secondary.data.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    openY: rate.low,
                    template: compile(
                        contractType === 'CONTRACT'
                            ? this.createSpotTooltip()
                            : this.createContractTooltip(),
                    )({
                        ...rate,
                        average: this.toUSDFormat(rate.average),
                        high: this.toUSDFormat(rate.high),
                        low: this.toUSDFormat(rate.low),
                        date: new Date(rate.date).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            timeZone: 'UTC',
                        }),
                        title: `Historical ${
                            contractType === 'CONTRACT' ? 'Spot' : 'Contract'
                        }`,
                    }),
                };
            }),
        );

        let contributionSeries = null;
        if (contributions.length > 0) {
            contributionSeries = this.createScatterSeries(
                root,
                chart,
                dailyAxis,
                yAxis,
                contributions,
                data.default.color.hex,
                contractType,
                'My Company Reports',
                perMile,
            );
        }

        const tooltip: am5.Tooltip = defaultLineSeries.get('tooltip');

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div></div>');
        chart.plotContainer.set('tooltip', tooltip);

        defaultLineSeries
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(data.default.color.hex),
                strokeOpacity: 1,
                fillOpacity: 1,
            });

        defaultLineSeries
            .get('tooltip')
            .get('background')
            .adapters.add('stroke', function (_fill, _target) {
                if (secondaryLineSeries?.isHidden()) {
                    return am5.color(data.default.color.hex);
                }
                return am5.color('#636D79');
            });

        defaultLineSeries.get('tooltip').label.adapters.add('dy', (dy) => {
            defaultLineSeries
                .get('tooltip')
                .adapters.add('bounds', function () {
                    return {
                        left: 70,
                        right: 9999,
                        top: 200,
                        bottom: 20,
                    };
                });
            return dy;
        });
        defaultLineSeries.get('tooltip').adapters.add('pointTo', (pointTo) => {
            return {
                ...pointTo,
                y: 20,
            };
        });

        defaultLineSeries
            .get('tooltip')
            .label.adapters.add('html', function (text, _target) {
                text = '';
                chart.series.each(function (series) {
                    var tooltipDataItem = series.get('tooltipDataItem');
                    const template = tooltipDataItem?.dataContext?.['template'];
                    if (
                        !!template?.length &&
                        series.isVisible() &&
                        series !== contributionSeries
                    ) {
                        text += tooltipDataItem.dataContext['template'];
                    }
                });
                return `<div class="flex flex-row gap-4">${text}</div>`;
            });

        // Add legend
        this.createLegend(
            chart,
            root,
            defaultLineSeries,
            secondaryLineSeries,
            secondaryCandlestick,
            false,
            contractType,
            dbId,
            contributionSeries,
        );

        // Add cursor
        !!contributionSeries
            ? this.addCursor(
                  root,
                  [defaultLineSeries, contributionSeries],
                  chart,
                  -5,
                  xAxis,
              )
            : this.addCursor(root, [defaultLineSeries], chart, -15, xAxis);

        secondaryLineSeries.hide();
        secondaryCandlestick.hide();
        if (hideContributions) {
            contributionSeries?.hide();
        } else {
            contributionSeries?.show();
        }
        return chart;
    }
    getMinMaxYValues(
        rateArrays: Array<Rate[]>,
        contributions: Contribution[],
    ): { min: any; max: any } {
        const rates = rateArrays.flat();
        const min = Math.min(
            ...rates.map((rate) => rate.low),
            ...contributions.map((contribution) => contribution.value),
        );
        const max = Math.max(
            ...rates.map((rate) => rate.high),
            ...contributions.map((contribution) => contribution.value),
        );
        return { min, max };
    }

    createLegend(
        chart: am5xy.XYChart,
        root: am5.Root,
        defaultMainSeries: am5xy.LineSeries,
        secondaryMainSeries: am5xy.LineSeries,
        secondarySecondarySeries: am5xy.CandlestickSeries | am5xy.LineSeries,
        isForeCast: Boolean,
        contractType: ContractTypeEnum,
        dbId: string,
        contributions?: am5xy.LineSeries,
    ) {
        const legendContainer = am5.Container.new(root, {
            layout: root.horizontalLayout,
        });

        chart.children.push(legendContainer);
        var staticLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'none',
                layout: root.horizontalLayout,
            }),
        );

        staticLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        staticLegend.markers.template.setAll({
            height: 3,
        });
        staticLegend.itemContainers.template.setAll({ paddingRight: -10 });

        var clickableLegend = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'marker',
                layout: root.horizontalLayout,
            }),
        );
        clickableLegend.markers.template.setAll({
            height: 3,
        });

        clickableLegend.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        clickableLegend.labels.template.states.create('disabled', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });

        clickableLegend.itemContainers.template.set('setStateOnChildren', true);

        clickableLegend.itemContainers.template.setAll({ paddingRight: -10 });
        //Add legend checkmark using svg path
        clickableLegend.markers.template.setup = this._checkboxMarkers(root);

        if (isForeCast) {
            clickableLegend.labels.template.setup = this._helpIcon(
                root,
                contractType === 'SPOT'
                    ? ContractTypeEnum.Contract
                    : ContractTypeEnum.Spot,
            );
            clickableLegend.labels.template.set(
                'tooltipText',
                '[bold fontSize: 12px]The shaded area represents Median Absolute Error (MAE)[/]',
            );
            clickableLegend.labels.template.set('tooltipPosition', 'pointer');
            clickableLegend.labels.template.states.create('hover', {
                fontSize: 12,
                fontWeight: '300',
                fill: am5.color('#0032A8'),
            });

            staticLegend.labels.template.setup = this._helpIcon(
                root,
                contractType,
            );
            staticLegend.labels.template.set(
                'tooltipText',
                '[bold fontSize: 12px]The shaded area represents Median Absolute Error (MAE)[/]',
            );
            staticLegend.labels.template.set('tooltipPosition', 'pointer');
            staticLegend.labels.template.states.create('hover', {
                fontSize: 12,
                fontWeight: '300',
                fill: am5.color('#0032A8'),
            });
        }

        clickableLegend.markers.template.events.on('click', function (ev) {
            var targetSeries = ev.target.dataItem.dataContext as am5xy.XYSeries;
            if (targetSeries === secondaryMainSeries) {
                if (secondarySecondarySeries?.isHidden()) {
                    secondarySecondarySeries.show();
                } else {
                    secondarySecondarySeries.hide();
                }
            }
        });
        if (contributions) {
            const contributionsLegend = legendContainer.children.push(
                am5.Legend.new(root, {
                    useDefaultMarker: true,
                    clickTarget:
                        contributions.data.length > 0 ? 'marker' : 'none',
                }),
            );
            // Circle marker next to contributions label
            contributionsLegend.markerRectangles.template.setAll({
                cornerRadiusTL: 10,
                cornerRadiusTR: 10,
                cornerRadiusBL: 10,
                cornerRadiusBR: 10,
                fillOpacity: 0,
                strokeWidth: 2,
                height: 10,
                width: 10,
                y: -3,
                x: 5,
            });
            contributionsLegend.markers.template.setAll({
                y: 12,
            });
            contributionsLegend.labels.template.setAll({
                fontSize: 12,
                fontWeight: '300',
                fill: am5.color('#192129'),
            });
            contributionsLegend.labels.template.states.create('disabled', {
                fontSize: 12,
                fontWeight: '300',
                fill: am5.color('#192129'),
            });
            if (contributions.data.length > 0) {
                contributionsLegend.markers.template.setup =
                    this._checkboxMarkers(root);
            }
            contributionsLegend.data.setAll([contributions]);
            staticLegend.itemContainers.template.setAll({ paddingTop: 12 });
            clickableLegend.itemContainers.template.setAll({ paddingTop: 12 });
            /**
             * Will add this without the db in this task:
             * https://transcore.jira.com/browse/EYEQ-7376
             */
            // contributionsLegend.events.on("click", (ev) => {
            //     var targetSeries = ev.target.dataItems[0].dataContext as am5xy.XYSeries;
            //     if (targetSeries === contributions) {
            //         if (contributions.isHidden()) {
            //             this._db.cacheData$(dbId, { hideContributions: true });
            //         } else {
            //             this._db.cacheData$(dbId, { hideContributions: false });
            //         }
            //     }
            //   }, this);
        }

        staticLegend.data.setAll([defaultMainSeries]);
        clickableLegend.data.setAll([secondaryMainSeries]);
    }

    private createHybridLegend(
        chart: am5xy.XYChart,
        root: am5.Root,
        defaultForecastLine: am5xy.LineSeries,
        defaultForecastArea: am5xy.LineSeries,
        defaultHistoricalLine: am5xy.LineSeries,
        defaultHistoricalCandlesticks:
            | am5xy.CandlestickSeries
            | am5xy.LineSeries,
        secondaryForecastLine: am5xy.LineSeries,
        secondaryForecastArea: am5xy.LineSeries,
        secondaryHistoricalLine: am5xy.LineSeries,
        secondaryHistoricalCandlesticks:
            | am5xy.CandlestickSeries
            | am5xy.LineSeries,
        contractType: ContractTypeEnum,
    ) {
        const legendContainer = am5.Container.new(root, {
            layout: root.horizontalLayout,
        });

        chart.children.push(legendContainer);
        const staticLegend1 = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'none',
                layout: root.horizontalLayout,
            }),
        );
        staticLegend1.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        staticLegend1.markers.template.setAll({
            height: 3,
        });
        staticLegend1.itemContainers.template.setAll({ paddingRight: -10 });

        staticLegend1.labels.template.setup = this._helpIcon(
            root,
            contractType,
        );
        staticLegend1.labels.template.set(
            'tooltipText',
            '[bold fontSize: 12px]The shaded area represents Median Absolute Error (MAE)[/]',
        );
        staticLegend1.labels.template.set('tooltipPosition', 'pointer');
        staticLegend1.labels.template.states.create('hover', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#0032A8'),
        });

        const staticLegend2 = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'none',
                layout: root.horizontalLayout,
            }),
        );
        staticLegend2.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        staticLegend2.markers.template.setAll({
            height: 1,
        });
        staticLegend2.itemContainers.template.setAll({
            paddingRight: -10,
            paddingLeft: -20,
        });

        var clickableLegend1 = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'marker',
                layout: root.horizontalLayout,
            }),
        );
        clickableLegend1.markers.template.setAll({
            height: 3,
        });
        clickableLegend1.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        clickableLegend1.labels.template.states.create('disabled', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        clickableLegend1.itemContainers.template.setAll({ paddingRight: -10 });
        //Add legend checkmark using svg path
        clickableLegend1.markers.template.setup = this._checkboxMarkers(root);
        clickableLegend1.labels.template.setup = this._helpIcon(
            root,
            contractType === 'SPOT'
                ? ContractTypeEnum.Contract
                : ContractTypeEnum.Spot,
        );
        clickableLegend1.labels.template.set(
            'tooltipText',
            '[bold fontSize: 12px]The shaded area represents Median Absolute Error (MAE)[/]',
        );
        clickableLegend1.labels.template.set('tooltipPosition', 'pointer');
        clickableLegend1.labels.template.states.create('hover', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#0032A8'),
        });
        clickableLegend1.markers.template.events.on('click', function (ev) {
            const targetSeries = ev.target.dataItem
                .dataContext as am5xy.XYSeries;
            if (targetSeries === secondaryForecastLine) {
                if (secondaryForecastArea?.isHidden()) {
                    secondaryForecastArea.show();
                } else {
                    secondaryForecastArea.hide();
                }
            }

            if (targetSeries === secondaryHistoricalLine) {
                if (secondaryHistoricalCandlesticks?.isHidden()) {
                    secondaryHistoricalCandlesticks.show();
                } else {
                    secondaryHistoricalCandlesticks.hide();
                }
            }
        });

        var clickableLegend2 = legendContainer.children.push(
            am5.Legend.new(root, {
                clickTarget: 'marker',
                layout: root.horizontalLayout,
            }),
        );
        clickableLegend2.markers.template.setAll({
            height: 3,
        });
        clickableLegend2.labels.template.setAll({
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        clickableLegend2.labels.template.states.create('disabled', {
            fontSize: 12,
            fontWeight: '300',
            fill: am5.color('#192129'),
        });
        clickableLegend2.itemContainers.template.setAll({ paddingRight: -10 });
        //Add legend checkmark using svg path
        clickableLegend2.markers.template.setup = this._checkboxMarkers(root);
        clickableLegend2.markers.template.events.on('click', function (ev) {
            const targetSeries = ev.target.dataItem
                .dataContext as am5xy.XYSeries;
            if (targetSeries === secondaryForecastLine) {
                if (secondaryForecastArea?.isHidden()) {
                    secondaryForecastArea.show();
                } else {
                    secondaryForecastArea.hide();
                }
            }

            if (targetSeries === secondaryHistoricalLine) {
                if (secondaryHistoricalCandlesticks?.isHidden()) {
                    secondaryHistoricalCandlesticks.show();
                } else {
                    secondaryHistoricalCandlesticks.hide();
                }
            }
        });

        staticLegend1.data.setAll([defaultForecastLine]);
        staticLegend2.data.setAll([defaultHistoricalLine]);
        clickableLegend1.data.setAll([secondaryForecastLine]);
        clickableLegend2.data.setAll([secondaryHistoricalLine]);
    }

    private createHistoricalSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        data: Rate[],
        color: { hex: GraphColors; name: string },
        contractType: ContractTypeEnum,
        name: string,
        isSecondary: boolean,
    ): {
        lineSeries: am5xy.LineSeries;
        candlestickSeries: am5xy.CandlestickSeries;
    } {
        const candlestickSeries = this.createCandlestickSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data,
            color.hex,
            isSecondary,
        );

        const lineSeries = this.createLineSeries(
            root,
            chart,
            xAxis,
            yAxis,
            data,
            color.hex,
            contractType,
            name,
            !!candlestickSeries,
        );

        return { lineSeries, candlestickSeries };
    }

    private addCursor(
        root: am5.Root,
        series: am5xy.XYSeries[],
        chart: am5xy.XYChart,
        verticalShift: number = 0,
        xAxis:
            | am5xy.DateAxis<am5xy.AxisRenderer>
            | am5xy.CategoryAxis<am5xy.AxisRenderer>,
    ) {
        const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
            behavior: 'none',
            snapToSeries: series,
            snapToSeriesBy: 'x',
            xAxis,
        });
        cursor.lineY.setAll({
            visible: false,
        });
        chart.set('cursor', cursor);
        cursor.lineX.setAll({
            strokeDasharray: null,
            strokeWidth: 1,
            stroke: am5.color('#636d79'),
            dy: verticalShift,
        });
    }

    private createScatterSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        contributions: Contribution[],
        color: string,
        contractType: ContractTypeEnum,
        name: string,
        perMile: boolean,
    ): am5xy.LineSeries {
        const scatterSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: 'value',
                valueXField: 'date',
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: 'horizontal',
                    getFillFromSprite: false,
                    html: '{template}',
                }),
            }),
        );

        this.setLineSeriesColor(scatterSeries, color);
        scatterSeries.strokes.template.setAll({
            strokeWidth: 0,
            opacity: 0,
        });

        scatterSeries.bullets.push((root) => {
            return this.createBullet(root, scatterSeries);
        });

        scatterSeries.data.setAll(
            contributions
                .filter((cont) => {
                    return cont.rateType === contractType;
                })
                .map((cont) => {
                    return {
                        date: this.mapDateTime(cont.date, false),
                        value: cont.value,
                        template: compile(this.createTooltipContribution())({
                            date: new Date(cont.date).toLocaleString('en-US', {
                                timeZone: 'UTC',
                                day: '2-digit',
                                year: 'numeric',
                                month: 'long',
                            }),
                            value: !perMile
                                ? this.toUSDFormat(cont.value)
                                : this.toUSDFormat(cont.value),
                            userType: cont.userType,
                            name: cont.shipperName,
                            contractType: cont.rateType,
                        }),
                    };
                }),
        );
        scatterSeries
            .get('tooltip')
            .get('background')
            .setAll({
                strokeWidth: 1,
                fill: am5.color('#FFFFFF'),
                stroke: am5.color(color),
                strokeOpacity: 1,
            });

        return scatterSeries;
    }

    private createBullet(root: am5.Root, series: am5xy.LineSeries): am5.Bullet {
        const circle = am5.Circle.new(root, {
            radius: 4,
            stroke: series.get('stroke'),
            fill: am5.color('#fff'),
            fillOpacity: 0,
        });

        return am5.Bullet.new(root, {
            sprite: circle,
        });
    }

    private createLineSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis:
            | am5xy.DateAxis<am5xy.AxisRenderer>
            | am5xy.CategoryAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        data: Rate[],
        color: string,
        contractType: ContractTypeEnum,
        name: string,
        hasCandlestickSeries?: boolean,
    ): am5xy.LineSeries {
        const tooltip: am5.Tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
        });
        const lineSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: 'value',
                valueXField: 'date',
                openValueYField: 'openY',
                tooltip: tooltip,
            }),
        );
        tooltip.get('background').set('strokeOpacity', 0);
        if (isEmpty(data)) return lineSeries;

        this.setLineSeriesColor(lineSeries, color);
        lineSeries.strokes.template.setAll({
            strokeWidth: 2,
        });

        hasCandlestickSeries ? this.addSquare(lineSeries, root, color) : noop();
        lineSeries?.data?.setAll(
            data?.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    value: rate.average,
                    high: rate.high,
                    low: rate.low,
                };
            }),
        );

        return lineSeries;
    }

    private mapDateTime(date: string, monthly: boolean) {
        const localDate = new Date(date);
        const utcDate = new Date(
            Date.UTC(
                localDate.getUTCFullYear(),
                localDate.getUTCMonth(),
                monthly ? 15 : localDate.getUTCDate() + 1,
            ),
        );
        return utcDate.getTime();
    }

    private createCandlestickSeries(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        data: Rate[] = [],
        color: string,
        isSecondary: boolean,
    ): am5xy.CandlestickSeries {
        const candlestickSeries = chart.series.push(
            am5xy.CandlestickSeries.new(root, {
                name: 'Candlesticks',
                xAxis,
                yAxis,
                openValueYField: 'open',
                highValueYField: 'high',
                lowValueYField: 'low',
                valueYField: 'close',
                valueXField: 'date',
                clustered: false,
                layer: isSecondary ? 100 : 0,
            }),
        );
        this.setCandlestickColor(candlestickSeries, color);

        this.addBottomLine(candlestickSeries, root, color)
            .addTopLine(candlestickSeries, root, color)
            .setWidth(candlestickSeries);
        candlestickSeries.bulletsContainer.set('layer', isSecondary ? 100 : 0);
        candlestickSeries.data.setAll(
            data?.map((rate) => {
                return {
                    date: this.mapDateTime(rate.date, true),
                    open: rate.high,
                    close: rate.low,
                    high: rate.high,
                    low: rate.low,
                };
            }),
        );
        return candlestickSeries;
    }

    private setCandlestickColor(
        series: am5xy.CandlestickSeries,
        color: string,
    ) {
        series.columns.template.states.create('riseFromOpen', {
            fill: am5.color(color),
            stroke: am5.color(color),
        });
        series.columns.template.states.create('dropFromOpen', {
            fill: am5.color(color),
            stroke: am5.color(color),
        });
    }
    private setLineSeriesColor(series: am5xy.LineSeries, color: string) {
        series.set('stroke', am5.color(color));
        series.set('fill', am5.color(color));
    }

    private addSquare(
        series: am5xy.LineSeries,
        root: am5.Root,
        color: string,
    ): this {
        series.bullets.push(() => {
            return am5.Bullet.new(root, {
                sprite: am5.Rectangle.new(root, {
                    fill: am5.color(color),
                    width: 8,
                    height: 8,
                    centerX: am5.percent(50),
                    centerY: am5.percent(50),
                }),
            });
        });

        return this;
    }

    private addBottomLine(
        series: am5xy.CandlestickSeries,
        root: am5.Root,
        color: string,
    ): this {
        series.bullets.push(() => {
            return am5.Bullet.new(root, {
                sprite: this.createHorizontalLine(root, color),
                locationY: 0,
            });
        });

        return this;
    }
    private addTopLine(
        series: am5xy.CandlestickSeries,
        root: am5.Root,
        color: string,
    ): this {
        series.bullets.push(() => {
            return am5.Bullet.new(root, {
                sprite: this.createHorizontalLine(root, color),
                locationY: 1,
            });
        });

        return this;
    }
    private setWidth(series: am5xy.CandlestickSeries): this {
        series.columns.template.setAll({
            width: 1,
        });
        return this;
    }
    private createHorizontalLine(root: am5.Root, color: string): am5.Rectangle {
        return am5.Rectangle.new(root, {
            fill: am5.color(color),
            width: 8,
            height: 1,
            centerX: am5.percent(50),
            centerY: am5.percent(50),
        });
    }
    private sortDataByDateAsc(a, b) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
    }
    private toUSDFormat(number: number) {
        const digits = number < 50 ? 2 : 0;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(number);
    }

    private _checkboxMarkers(
        root: am5.Root,
    ): <O extends am5.Container>(entity: O) => void {
        return function (marker) {
            let check = am5.Graphics.new(root, {
                fill: am5.color('#0046e0'),
                fillOpacity: 1,
                svgPath:
                    'M -10 -7 C -8.3431 -7 -7 -5.6569 -7 -4 L -7 8 C -7 9.6569 -8.3431 11 -10 11 L -22 11 C -23.6569 11 -25 9.6569 -25 8 L -25 -4 C -25 -5.6569 -23.6569 -7 -22 -7 L -10 -7 Z M -11.5303 -2.0303 L -18 4.4393 L -20.4697 1.9697 C -20.7626 1.6768 -21.2374 1.6768 -21.5303 1.9697 C -21.8232 2.2626 -21.8232 2.7374 -21.5303 3.0303 L -18.5303 6.0303 C -18.2374 6.3232 -17.7626 6.3232 -17.4697 6.0303 L -10.4697 -0.9697 C -10.1768 -1.2626 -10.1768 -1.7374 -10.4697 -2.0303 C -10.7626 -2.3232 -11.2374 -2.3232 -11.5303 -2.0303 Z',
            });
            check.states.create('disabled', {
                fill: am5.color('#fff'),
                fillOpacity: 1,
                stroke: am5.color('#636D79'),
                strokeWidth: 2,
                svgPath:
                    'M -10 -3.8 Z Z M -21.7 -6.5 H -9.1 C -8.11 -6.5 -7.3 -5.69 -7.3 -4.7 V 7.9 C -7.3 8.89 -8.11 9.7 -9.1 9.7 H -21.7 C -22.69 9.7 -23.5 8.89 -23.5 7.9 V -4.7 C -23.5 -5.69 -22.69 -6.5 -21.7 -6.5 Z',
            });

            marker.children.push(check);
        };
    }

    private _helpIcon(
        root: am5.Root,
        contractType: ContractTypeEnum,
    ): <O extends am5.Container>(entity: O) => void {
        return function (marker) {
            let info: am5.Graphics = null;
            if (contractType === 'SPOT') {
                info = am5.Graphics.new(root, {
                    fill: am5.color('#FFFFFF'),
                    fillOpacity: 1,
                    stroke: am5.color('#636D79'),
                    strokeWidth: 0.8,
                    svgPath: spotInfoLabelSvgPath,
                });
            } else {
                info = am5.Graphics.new(root, {
                    fill: am5.color('#FFFFFF'),
                    fillOpacity: 1,
                    stroke: am5.color('#636D79'),
                    strokeWidth: 0.8,
                    svgPath: contractInfoLabelSvgPath,
                });
            }

            marker.children.push(info);
        };
    }
}
